<script setup lang="ts">
/**
 * QuickPop · 顶栏图标的悬停速览浮层
 * ------------------------------------------------------------
 * 纯展示组件：标题 + 最多 5 条消息速览 + 底部「忽略」。
 * 显隐与悬停延迟由 PortalHeader 控制（show），浮层是触发容器的后代，
 * 鼠标移入浮层不会触发外层 mouseleave。
 */
import { FEED_TITLE, type FeedKind, type QuickItem } from './portal/quick-feed'

defineProps<{ kind: FeedKind; items: QuickItem[]; show: boolean }>()
const emit = defineEmits<{
  (e: 'open', item: QuickItem): void
  (e: 'ignore'): void
}>()
</script>

<template>
  <span v-if="show" class="qf-pop" role="tooltip">
    <span class="qf-h">
      <b>{{ FEED_TITLE[kind] }}</b>
      <i v-if="items.length">{{ items.length }} 条未读</i>
    </span>
    <ul v-if="items.length" class="qf-list">
      <li v-for="it in items" :key="it.id">
        <button type="button" class="qf-item" @click="emit('open', it)">
          <i class="qf-dot" />
          <span class="qf-b">
            <b>{{ it.title }}</b>
            <small>{{ it.sub }}</small>
          </span>
          <time>{{ it.time }}</time>
        </button>
      </li>
    </ul>
    <p v-else class="qf-empty">暂无新消息</p>
    <button type="button" class="qf-ignore" :disabled="!items.length" @click="emit('ignore')">
      忽略{{ items.length ? `全部 ${items.length} 条提醒` : '' }}
    </button>
  </span>
</template>

<style scoped>
.qf-pop {
  position: absolute; top: calc(100% + 8px); right: -10px; z-index: 340;
  width: 320px; max-width: calc(100vw - 24px);
  display: flex; flex-direction: column;
  padding: 10px; border: 1px solid var(--line); border-radius: 12px;
  background: var(--surface); color: var(--ink);
  box-shadow: var(--sh-3);
  animation: qfIn .16s ease;
}
@keyframes qfIn { from { transform: translateY(-4px); opacity: 0; } to { transform: none; opacity: 1; } }
.qf-h { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding: 2px 4px 8px; border-bottom: 1px solid var(--line-soft); }
.qf-h b { font-size: 13px; font-weight: 650; color: var(--ink); }
.qf-h i { font-style: normal; font-size: 11px; color: var(--ink-4); }
.qf-list { list-style: none; margin: 0; padding: 4px 0; max-height: 260px; overflow-y: auto; }
.qf-item { display: flex; align-items: flex-start; gap: 9px; width: 100%; text-align: left; padding: 9px 6px; border-radius: 8px; }
.qf-item:hover { background: var(--hover); }
.qf-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: #ff5c5c; margin-top: 6px; }
.qf-b { flex: 1; min-width: 0; }
.qf-b b { display: block; font-size: 12.5px; font-weight: 600; color: var(--ink); line-height: 1.4;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qf-b small { display: block; font-size: 11.5px; color: var(--ink-4); margin-top: 2px; line-height: 1.45;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qf-item time { flex: none; font-size: 10.5px; color: var(--ink-4); margin-top: 2px; }
.qf-empty { padding: 22px 8px 14px; color: var(--ink-3); font-size: 13px; text-align: center; }
.qf-ignore {
  flex: none; margin-top: 4px; height: 30px; border-radius: 8px;
  border: 1px solid var(--line); background: var(--surface-2);
  font-size: 12.5px; color: var(--ink-2);
}
.qf-ignore:hover:not(:disabled) { background: var(--surface-3); color: var(--ink); }
.qf-ignore:disabled { color: var(--ink-4); cursor: default; }
</style>
